import React, { useState, useEffect, useCallback, useRef } from "react";
import PromptsTemplate from "components/templates/PromptsTemplate";
import { Prompt, PromptType } from "common/models/types";
import {
  fetchPrompts,
  fetchPromptById,
  togglePromptActive,
  deletePrompt,
} from "api/promptService";
import Spinner from "components/atoms/Spinner";
import { ToastManager } from "components/organisms/CommonUI";
import { isDoseExtractPromptType } from "common/models/doseExtractPrompt";
import { log, LogLevel } from "utils/LogUtil";
import {
  PAGE,
  TypeListState,
  EMPTY_TYPE_STATE,
  buildTypeCounts,
  appendPage,
  applyCountAfterCrud,
  detectCountReliable,
  CountResult,
} from "./promptListState";

const Prompts: React.FC = () => {
  // 종류별 버전 수 (totalItems > 0 인 타입만 보유). 폴백 모드에서는 enum 전체를 헤더로 노출.
  const [typeCounts, setTypeCounts] = useState<Partial<Record<PromptType, number>>>({});
  // 펼친 타입의 로드 상태
  const [typeLists, setTypeLists] = useState<Partial<Record<PromptType, TypeListState>>>({});
  // 카운트가 promptType 필터를 반영하지 못하는 백엔드일 때의 폴백 모드
  const [countReliable, setCountReliable] = useState<boolean>(true);
  // 카운트 부분 실패/폴백 안내
  const [countError, setCountError] = useState<string | null>(null);

  // 선택된 프롬프트 상태
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  // 초기 로딩(카운트 수집)
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // 상세 정보 로딩
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  // 이미 첫 페이지를 로드한 타입 (접었다 펴도 재요청 안 함)
  const loadedTypes = useRef<Set<PromptType>>(new Set());
  // 같은 타입 동시 요청 가드
  const inFlight = useRef<Set<PromptType>>(new Set());

  // typeLists 미러: 콜백을 안정 정체성으로 유지하면서 최신 리스트를 읽기 위함
  const typeListsRef = useRef(typeLists);
  useEffect(() => {
    typeListsRef.current = typeLists;
  }, [typeLists]);

  // 펼쳐진 리스트에서 promptId가 속한 타입을 찾는다(토글/삭제 시 타입 확정용)
  const findLoadedTypeByPromptId = useCallback((promptId: number): PromptType | undefined => {
    const lists = typeListsRef.current;
    for (const key of Object.keys(lists) as PromptType[]) {
      if (lists[key]?.items.some((p) => p.id === promptId)) return key;
    }
    return undefined;
  }, []);

  // 특정 타입의 페이지를 로드(또는 첫 페이지 재로드)
  const loadTypePage = useCallback(async (type: PromptType, isInitial: boolean) => {
    if (inFlight.current.has(type)) return;
    inFlight.current.add(type);

    // 직전 items 길이를 함수형 업데이트로 안전하게 읽어 offset 산정
    let offset = 0;
    setTypeLists((prev) => {
      const cur = prev[type];
      offset = isInitial || !cur ? 0 : cur.items.length;
      return { ...prev, [type]: { ...(cur ?? EMPTY_TYPE_STATE), isLoading: true, error: false } };
    });

    try {
      const res = await fetchPrompts({
        promptType: type,
        offset,
        limit: PAGE,
        orderBy: "id", // id auto-increment → 최신 생성 우선
        orderDirection: "DESC", // 백엔드 enum은 대문자
      });
      const incoming = res.error === 0 && res.data ? res.data : [];
      setTypeLists((prev) => ({
        ...prev,
        [type]: appendPage(isInitial ? undefined : prev[type], incoming, isInitial, res.meta),
      }));
    } catch (e) {
      log(LogLevel.ERROR, `타입 ${type} 로드 실패:`, e);
      setTypeLists((prev) => ({
        ...prev,
        [type]: { ...(prev[type] ?? EMPTY_TYPE_STATE), isLoading: false, error: true },
      }));
    } finally {
      inFlight.current.delete(type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 의존성 없음 — 함수형 업데이트로 최신 상태 접근 (CI=true에서 exhaustive-deps 경고 차단 방지)

  // 진입 시 종류별 카운트 수집
  const loadCounts = useCallback(async () => {
    setIsLoading(true);
    const activeTypes = Object.values(PromptType);
    const results = await Promise.allSettled<CountResult>(
      activeTypes.map(async (t) => {
        const res = await fetchPrompts({ promptType: t, limit: 1, offset: 0 });
        return { type: t, total: res.meta?.totalItems ?? res.data?.length ?? 0 };
      })
    );

    // 신뢰성 자가 판정: 서로 다른 종류의 totalItems가 모두 같고 크면 필터 미반영 의심
    const totals = results
      .filter((r): r is PromiseFulfilledResult<CountResult> => r.status === "fulfilled")
      .map((r) => r.value.total);
    const reliable = detectCountReliable(totals);
    setCountReliable(reliable);

    const { counts, failed } = buildTypeCounts(results);
    if (reliable) {
      setTypeCounts(counts);
      setCountError(failed > 0 ? `${failed}개 종류의 카운트 조회에 실패했습니다.` : null);
    } else {
      // 폴백: enum 전체를 헤더로 노출(카운트 미표시), 펼친 뒤 비면 사후 숨김
      const all: Partial<Record<PromptType, number>> = {};
      activeTypes.forEach((t) => {
        all[t] = 0;
      });
      setTypeCounts(all);
      setCountError("카운트를 신뢰할 수 없어 모든 종류를 표시합니다. 펼치면 실제 항목을 확인합니다.");
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  // 카운트만 재조회하여 갱신(0이면 헤더 제거)
  const refreshTypeCount = useCallback(async (type: PromptType) => {
    try {
      const res = await fetchPrompts({ promptType: type, limit: 1, offset: 0 });
      const total = res.meta?.totalItems ?? res.data?.length ?? 0;
      setTypeCounts((prev) => applyCountAfterCrud(prev, type, total));
      if (total === 0) loadedTypes.current.delete(type);
    } catch (e) {
      log(LogLevel.ERROR, `타입 ${type} 카운트 갱신 실패:`, e);
    }
  }, []);

  // 강제 첫 페이지 재로드(CRUD 후)
  const reloadType = useCallback(
    (type: PromptType) => {
      loadedTypes.current.add(type);
      loadTypePage(type, true);
    },
    [loadTypePage]
  );

  // 펼침: 처음 펼친 타입만 첫 페이지 로드
  const handleExpandType = useCallback(
    (type: PromptType) => {
      if (loadedTypes.current.has(type)) return;
      loadedTypes.current.add(type);
      loadTypePage(type, true);
    },
    [loadTypePage]
  );

  const handleLoadMoreType = useCallback(
    (type: PromptType) => loadTypePage(type, false),
    [loadTypePage]
  );

  const handleRetryType = useCallback(
    (type: PromptType) => loadTypePage(type, true),
    [loadTypePage]
  );

  // 프롬프트 선택
  const handleSelectPrompt = useCallback(
    async (prompt: Prompt) => {
      if (selectedPrompt && selectedPrompt.id === prompt.id) {
        setSelectedPrompt(null);
        return;
      }
      if (prompt.id) {
        try {
          setIsLoadingDetail(true);
          const detail = await fetchPromptById(prompt.id);
          setSelectedPrompt(detail);
        } catch (error) {
          log(LogLevel.ERROR, "프롬프트 상세 정보 로드 실패:", error);
          setSelectedPrompt(prompt);
        } finally {
          setIsLoadingDetail(false);
        }
      } else {
        setSelectedPrompt(prompt);
      }
    },
    [selectedPrompt]
  );

  // 새 프롬프트 생성(임시 항목을 우측 패널에 표시)
  const handleCreatePrompt = useCallback(() => {
    setSelectedPrompt({
      id: 0,
      version: "v1.0.0",
      promptTemplate: "",
      description: "",
      promptType: PromptType.FREE_QUESTION,
      model: "GPT_4O",
      isActive: false,
      createdAt: new Date(),
    });
  }, []);

  // 생성/수정 후 처리: 변경된 타입의 카운트 갱신 + (펼쳐져 있으면) 재로드
  const handleUpdatePrompt = useCallback(
    async (updatedPrompt: Prompt) => {
      if (!updatedPrompt.id) {
        log(LogLevel.ERROR, "업데이트된 프롬프트에 ID가 없습니다.");
        return;
      }
      const newType = updatedPrompt.promptType || PromptType.FREE_QUESTION;
      const oldType = selectedPrompt?.promptType; // onUpdate 호출 시점엔 변경 전 타입
      const wasNew = selectedPrompt?.id === 0;
      const typeChanged = !!oldType && oldType !== newType;

      // 새 타입 카운트 갱신 + (새로 생성됐거나 펼쳐져 있으면) 재로드
      await refreshTypeCount(newType);
      if (wasNew || loadedTypes.current.has(newType)) reloadType(newType);

      // 타입이 바뀐 수정이면 구 타입도 카운트 감소/재로드 (계획 6-6)
      if (typeChanged && oldType) {
        await refreshTypeCount(oldType);
        if (loadedTypes.current.has(oldType)) reloadType(oldType);
      }

      try {
        const detail = await fetchPromptById(updatedPrompt.id);
        setSelectedPrompt(detail);
      } catch (error) {
        log(LogLevel.ERROR, "수정된 프롬프트 상세 로드 실패:", error);
        setSelectedPrompt(updatedPrompt);
      }
    },
    [refreshTypeCount, reloadType, selectedPrompt?.id, selectedPrompt?.promptType]
  );

  // 새로고침(상세 패널의 reload 버튼)
  const handleReloadPrompt = useCallback(
    async (promptId: number) => {
      try {
        setIsLoadingDetail(true);
        const updated = await fetchPromptById(promptId);
        setSelectedPrompt(updated);
        const type = updated.promptType || PromptType.FREE_QUESTION;
        await refreshTypeCount(type);
        if (loadedTypes.current.has(type)) reloadType(type);
      } catch (error) {
        log(LogLevel.ERROR, "프롬프트 새로고침 실패:", error);
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [refreshTypeCount, reloadType]
  );

  // 활성화 토글: 서버의 활성 배타성 가정 → 해당 타입 전체 재로드
  const handleToggleActive = useCallback(
    async (promptId: number, isActive: boolean) => {
      // 펼쳐진 리스트에서 실제 타입을 먼저 확정(응답의 promptType은 누락될 수 있다)
      const loadedType = findLoadedTypeByPromptId(promptId);
      // dose 추출 타입의 "끄기" 클릭(isActive=false)은 서버를 부르지 않는다(D-1). 목록 스위치는
      // 현행상 그 행을 켜고 나머지를 끄는 활성화 버튼이라, 끄기 클릭이 오히려 그 행을 활성화하는
      // 역동작이 된다 — 오클릭 한 번에 운영 지시문이 바뀌는 일을 막는다.
      if (!isActive && isDoseExtractPromptType(loadedType)) {
        ToastManager.show({
          content: "끄려면 편집기에서 활성화를 끄고 저장하세요",
          duration: 3000,
        });
        return;
      }
      try {
        const updated = await togglePromptActive(promptId, isActive);
        const type = loadedType ?? updated.promptType;
        if (type && loadedTypes.current.has(type)) reloadType(type);
        setSelectedPrompt((prev) => (prev && prev.id === promptId ? updated : prev));
      } catch (error) {
        // 활성화 거절(dose 타입 표식·모델 검증 실패 등) 봉투 message를 토스트로 보인다(기존엔 삼켰음).
        log(LogLevel.ERROR, "프롬프트 활성화 상태 변경 실패:", error);
        ToastManager.show({
          content: error instanceof Error ? error.message : "프롬프트 상태 변경에 실패했습니다.",
          duration: 3000,
        });
      }
    },
    [reloadType, findLoadedTypeByPromptId]
  );

  // 삭제: 카운트 갱신, 펼친 리스트에서 제거, 선택 해제
  const handleDeletePrompt = useCallback(
    async (promptId: number) => {
      const type = selectedPrompt?.promptType || PromptType.FREE_QUESTION;
      try {
        setIsLoadingDetail(true);
        await deletePrompt(promptId);
        await refreshTypeCount(type);
        if (loadedTypes.current.has(type)) reloadType(type);
        setSelectedPrompt(null);
      } catch (error) {
        log(LogLevel.ERROR, "프롬프트 삭제 실패:", error);
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [refreshTypeCount, reloadType, selectedPrompt?.promptType]
  );

  if (isLoading && Object.keys(typeCounts).length === 0) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <PromptsTemplate
      typeCounts={typeCounts}
      typeLists={typeLists}
      countReliable={countReliable}
      countError={countError}
      selectedPrompt={selectedPrompt}
      isLoadingDetail={isLoadingDetail}
      onSelectPrompt={handleSelectPrompt}
      onCreatePrompt={handleCreatePrompt}
      onExpandType={handleExpandType}
      onLoadMoreType={handleLoadMoreType}
      onRetryType={handleRetryType}
      onToggleActive={handleToggleActive}
      onUpdatePrompt={handleUpdatePrompt}
      onReloadPrompt={handleReloadPrompt}
      onDeletePrompt={handleDeletePrompt}
    />
  );
};

export default Prompts;
